import { useCallback, useEffect, useState } from 'react';
import protocolSubcategoryApi from '../api/protocol-subcategory-api';

export const toSubcategoryOption = (item) => ({
  value: item._id,
  label: item.name?.sr,
  category: item.category,
  isActive: item.isActive,
});

// Protocol subcategories as select options (`{ value, label, category, isActive }`),
// keyed by id. Dropdowns load them a page at a time, so this keeps every item
// seen so far (`register`) and fetches the given `ids` it doesn't know yet:
// tags already on a protocol, which may not be on any loaded page.
export const useSubcategoryLookup = (ids) => {
  const [known, setKnown] = useState({});
  const idsKey = [...new Set((ids || []).filter(Boolean))].sort().join(',');

  useEffect(() => {
    const missing = idsKey ? idsKey.split(',').filter((id) => !known[id]) : [];
    if (!missing.length) return undefined;
    let cancelled = false;
    protocolSubcategoryApi
      .getAll(1, missing.length, { ids: missing })
      .then(({ data, success }) => {
        if (cancelled || !success) return;
        setKnown((prev) => ({
          ...prev,
          ...Object.fromEntries(data.items.map((item) => [item._id, toSubcategoryOption(item)])),
        }));
      });
    return () => {
      cancelled = true;
    };
    // `known` is left out on purpose: re-running on every registered page
    // would refetch ids the API doesn't return (deleted items).
    // eslint-disable-next-line
  }, [idsKey]);

  // Takes options already in `toSubcategoryOption` shape.
  const register = useCallback((options) => {
    if (!options?.length) return;
    setKnown((prev) => ({
      ...prev,
      ...Object.fromEntries(options.map((option) => [option.value, option])),
    }));
  }, []);

  return { known, register };
};
